import React, { useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import * as Sentry from "@sentry/react";
import { convertErrorsToString } from "src/utils/utils";
import { MobileHandOffUpload } from "@simplecitizen/gdl-regula-components";

// Types
export type ErrorObject = {
  [key: string]: {
    en: string;
  };
};

export type FieldError = {
  fieldName: string;
  error: string;
};

// Constants
const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL || "https://localhost:5003/api/workright";
const UPLOAD_FAILURE_THRESHOLD = Number(process.env.REACT_APP_UPLOAD_FAILURE_THRESHOLD) || 20;
const UPLOAD_RETRY_MESSAGE =
  "We’re having trouble uploading your document. In the open upload dialog on your desktop, click Refresh to scan the QR code again and retry.";

const DocumentUploadMobileHandoff = () => {
  const [searchParams] = useSearchParams();
  const { checkId, docId, sessionId } = useParams<{
    checkId: string;
    docId: string;
    sessionId: string;
  }>();
  // State
  const [uploadStatus, setUploadStatus] = useState<
    "initiated" | "success" | "error" | "loading"
  >("initiated");
  const [faceMisMatch, setFaceMisMatch] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>();
  const [selfieImage, setSelfieImage] = useState<string>();
  const [, setUploadFailureCount] = useState(0);

  // Search params - handle both normal and HTML-encoded parameter names
  const startScreen = searchParams.get("startScreen") || searchParams.get("amp;startScreen");
  const facialComparison = searchParams.get("facialComparison") || searchParams.get("amp;facialComparison");
  const preventForceSubmit = searchParams.get("preventForceSubmit") || searchParams.get("amp;preventForceSubmit");

  const resetUploadFailures = () => {
    setUploadFailureCount(0);
    setErrorMessage(undefined);
  };

  const handleUploadFailure = () => {
    setUploadFailureCount((prev) => {
      const nextFailureCount = prev + 1;
      if (nextFailureCount >= UPLOAD_FAILURE_THRESHOLD) {
        setErrorMessage(UPLOAD_RETRY_MESSAGE);
      }
      return nextFailureCount;
    });
  };

  // Validate returns 409 from a single branch: a document already exists for this session and the
  // desktop can already collect it. Reporting an error here would be misleading.
  const handleDuplicateUpload = (operation: "verifyDocument" | "forceDocument") => {
    Sentry.captureMessage("Mobile handoff upload rejected as a duplicate", {
      level: "info",
      tags: {
        component: 'DocumentUploadMobileHandoff',
        operation,
        checkId: checkId || 'unknown',
        docId: docId || 'unknown'
      },
      extra: { sessionId }
    });
    setUploadStatus("success");
    resetUploadFailures();
  };

  const verifyDocument = async ({
    docFrontSideImage, 
    selfieImage, 
    docBackSideImage 
  }: {
    docFrontSideImage: string;
    selfieImage: string;
    docBackSideImage?: string;
  }) => {
    setUploadStatus("loading");
    setFaceMisMatch(false);

    const images = [
      { format: "base64", key: "front", source: docFrontSideImage },
      { format: "base64", key: "selfie", source: selfieImage },
    ];

    if (docBackSideImage) {
      images.splice(1, 0, { format: "base64", key: "back", source: docBackSideImage });
    }

    const newDocument = {
      images,
      relationships: [
        {
          type: "mobile-handoff-session",
          id: sessionId,
        },
      ],
    };

    try {
      await axios.post(
        `${API_BASE_URL}/v1/checks/${checkId}/document-collectors/${docId}/Validate`,
        newDocument,
        {
          timeout: 120000
        }
      );
      setUploadStatus("success");
      resetUploadFailures();
    } catch (error: any) {
      if (error.response?.status === 409) {
        handleDuplicateUpload("verifyDocument");
        return;
      }

      // Log error to Sentry with mobile handoff context
      Sentry.captureException(error, {
        tags: {
          component: 'DocumentUploadMobileHandoff',
          operation: 'verifyDocument',
          checkId: checkId || 'unknown',
          docId: docId || 'unknown'
        },
        extra: {
          sessionId,
          startScreen,
          facialComparison,
          preventForceSubmit,
          hasBackSideImage: !!docBackSideImage,
          errorResponse: error?.response?.data,
          errorStatus: error?.response?.status,
          isFaceMismatch: !!error.response?.data?.errors?.["images.selfie"]
        }
      });

      if (error.response?.data?.errors) {
        if (error.response.data.errors["images.selfie"]) {
          setFaceMisMatch(true);
          setUploadStatus("initiated");
          return;
        }
        const convertedMessage = convertErrorsToString(error.response.data.errors);
        setErrorMessage(convertedMessage);
      }
      setUploadStatus("error");
      handleUploadFailure();
    }
  };

  const forceDocument = async ({ 
    docFrontSideImage,  
    docBackSideImage 
  }: {
    docFrontSideImage: string;
    docBackSideImage?: string;
  }) => {

    setFaceMisMatch(false);
    setUploadStatus("loading");

    const images = [
      { format: "base64", key: "front", source: docFrontSideImage },
      { format: "base64", key: "selfie", source: selfieImage },
    ];

    if (docBackSideImage) {
      images.splice(1, 0, { format: "base64", key: "back", source: docBackSideImage });
    }

    const newDocument = {
      images,
      status: "created",
      relationships: [
        {
          type: "mobile-handoff-session",
          id: sessionId,
        },
      ],
    };

    try {
      await axios.post(
        `${API_BASE_URL}/v1/checks/${checkId}/document-collectors/${docId}/Validate`,
        newDocument
      );
      setUploadStatus("success");
      resetUploadFailures();
    } catch (error: any) {
      if (error.response?.status === 409) {
        handleDuplicateUpload("forceDocument");
        return;
      }

      // Log error to Sentry with force document context
      Sentry.captureException(error, {
        tags: {
          component: 'DocumentUploadMobileHandoff',
          operation: 'forceDocument',
          checkId: checkId || 'unknown',
          docId: docId || 'unknown'
        },
        extra: {
          sessionId,
          startScreen,
          facialComparison,
          preventForceSubmit,
          hasBackSideImage: !!docBackSideImage,
          hasSelfieImage: !!selfieImage,
          errorResponse: error?.response?.data,
          errorStatus: error?.response?.status,
          hasReturnedDocument: !!error?.response?.data?.document
        }
      });

      const returnedDocument = error?.response?.data?.document;
      if (returnedDocument) {
        setUploadStatus("success");
        resetUploadFailures();
        return;
      }
      setUploadStatus("error");
      handleUploadFailure();
    }
  };
  return (
    <div className="h-full w-full flex items-center">
      <MobileHandOffUpload
        regulaLicense={process.env.REACT_APP_REGULA_LICENSE_KEY}
        selfieImage={`data:image/png;base64,${selfieImage}`}
        onForceSubmit={(fields) => 
          forceDocument({
            docFrontSideImage: fields?.docFrontSideImage?.split(",")[1],
            docBackSideImage: fields?.docBackSideRawBase64,
          })
        }
        errorMessage={errorMessage}
        status={uploadStatus}
        onProcessedFinished={(fields) => {
          const base64Data = fields?.docFrontSideImage?.split(",")[1];
          setSelfieImage(fields?.selfieImage);
          verifyDocument({
            docFrontSideImage: base64Data,
            selfieImage: fields?.selfieImage,
            docBackSideImage: fields?.docBackSideRawBase64,
          });
        }}
        uploadAttempts={1}
        faceMismatch={faceMisMatch}
        forceSubmit={preventForceSubmit !== "true"}
        facialComparison={facialComparison === "true"}
        settings={{
          startScreen: startScreen === "true",
        }}
      />
    </div>
  );
};

export default DocumentUploadMobileHandoff;
